import React, { useState, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { Combobox, Option, OptionGroup, Text } from '@fluentui/react-components';
import { Search20Regular } from '@fluentui/react-icons';
import useDebounce from '../../../utils/hooks/UseDebounce';
import { GetCompaniesByFilter } from '../../../utils/statApi/CompaniesApi';
import { LocalStorageKeys } from '../../../utils/hooks/UseLocalStorage';
import { IRecentCompaniesCardItem } from '../../../utils/types/IRecentCompaniesCardItem';
import { THROTTLE_SEARCH_TIMEOUT } from '../../../app/common/constants/SiteConstants';
import useStatHeaderStyles from './StatHeader.styles';
import UserContext from '../../../utils/authorization/UserContext';
import { StatUser } from '../../../utils/statApi/UsersApi';
import hasAccess from '../../../utils/authorization/authorizationCheck';

/** Value used by the non-selectable "no results" entry so it is never treated as a match. */
const NO_RESULTS_VALUE = '__no_results__';

/** Company codes are either 4 or 9 digits; typing one navigates straight to the company. */
const isCompanyCode = (value: string): boolean =>
  (value.length === 4 || value.length === 9) && parseInt(value, 10) > 0;

const getRecentCompanyCodes = (): string[] => {
  try {
    const companiesString = localStorage.getItem(LocalStorageKeys.recentCompanies);
    const recentCompanies: IRecentCompaniesCardItem[] = companiesString
      ? JSON.parse(companiesString)
      : [];
    return recentCompanies?.map((company) => company.companyCode) ?? [];
  } catch {
    return [];
  }
};

interface ICompanySearchResult {
  id: number;
  companyCode: string;
  entityLegalName: string;
}

/**
 * Company search for the site header.
 *
 * Built on Fluent's `Combobox`, which owns the combobox/listbox ARIA wiring, the
 * activedescendant keyboard model and popover positioning. That replaces the previous
 * Coherence `EnhancedSearch`, whose bugs were worked around with `getElementById`
 * attribute patching and a `setTimeout` that synthesised clicks on its internal buttons.
 *
 * The component has no notion of viewport width: on narrow screens the wrapper collapses
 * to an icon and re-expands on `:focus-within`, entirely in `StatHeader.styles.ts`.
 */
const StatHeaderSearch = (): JSX.Element => {
  const styles = useStatHeaderStyles();
  const history = useHistory();
  const user = useContext(UserContext) as unknown as StatUser;
  const defaultTab = hasAccess(user?.permissions, 'ProcessesList:View')
    ? 'processes'
    : 'company-information';

  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [recentCompanyCodes, setRecentCompanyCodes] = useState<string[]>(getRecentCompanyCodes);

  // Holds the option the user has arrowed onto. A ref rather than state because it must not
  // trigger a re-render on every arrow key, and only Enter handling ever reads it.
  const activeCompanyCodeRef = React.useRef<string | null>(null);

  const debouncedQuery = useDebounce(query.trim(), THROTTLE_SEARCH_TIMEOUT);

  const { data, loading } = useQuery(GetCompaniesByFilter, {
    variables: {
      companySearchDto: { keyword: debouncedQuery },
      paginationDto: { currentPage: 1, pageSize: 10 },
    },
    skip: debouncedQuery.length === 0,
    nextFetchPolicy: 'network-only',
  });

  const companies: ICompanySearchResult[] = data?.companies?.items ?? [];
  const hasQuery = query.trim().length > 0;
  const isStale = debouncedQuery !== query.trim();

  const goToCompany = (companyCode: string): void => {
    setQuery('');
    setIsOpen(false);
    activeCompanyCodeRef.current = null;
    history.push(`/companies/${companyCode}/${defaultTab}`);
  };

  const searchForFreeText = (): void => {
    const value = query.trim();

    if (isCompanyCode(value)) {
      goToCompany(value);
      return;
    }

    if (companies.length === 0) {
      setQuery('');
      setIsOpen(false);
      history.push('/companies?NotFound=true');
      return;
    }

    goToCompany(companies[0].companyCode);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key !== 'Enter' || !hasQuery) {
      return;
    }

    // When an option is highlighted, Fluent raises `onOptionSelect` for the same keypress
    // and that handler navigates — free-text search only applies when nothing is active.
    if (activeCompanyCodeRef.current) {
      return;
    }

    searchForFreeText();
  };

  const renderListboxContent = (): JSX.Element => {
    if (!hasQuery) {
      if (recentCompanyCodes.length === 0) {
        return (
          <Option value={NO_RESULTS_VALUE} disabled text="">
            <Text className={styles.listboxMessage}>No recent companies</Text>
          </Option>
        );
      }

      return (
        <OptionGroup label="Recent companies">
          {recentCompanyCodes.map((companyCode) => (
            <Option key={companyCode} value={companyCode} text={companyCode}>
              {companyCode}
            </Option>
          ))}
        </OptionGroup>
      );
    }

    if (companies.length === 0) {
      return (
        <Option value={NO_RESULTS_VALUE} disabled text="">
          <Text className={styles.listboxMessage}>
            {loading || isStale ? 'Searching…' : 'No companies found'}
          </Text>
        </Option>
      );
    }

    return (
      <OptionGroup label="Companies">
        {companies.map((company) => (
          <Option key={company.id} value={company.companyCode} text={company.companyCode}>
            <span>
              {company.companyCode}
              <span className={styles.optionDescription}>{company.entityLegalName}</span>
            </span>
          </Option>
        ))}
      </OptionGroup>
    );
  };

  return (
    <div className={styles.search} role="search" aria-label="Company search">
      <Combobox
        className={styles.combobox}
        listbox={{ className: styles.listbox }}
        input={{
          className: styles.comboboxInput,
          'aria-label': 'Search companies',
          onKeyDown: handleKeyDown,
        }}
        // The magnifier doubles as the collapsed affordance on narrow viewports, so it has
        // to be present at all times. `clearable` is deliberately not used: Fluent visually
        // hides `expandIcon` whenever the clear icon is showing, which would leave the
        // collapsed search with no icon at all. The query resets itself after navigating.
        expandIcon={{ children: <Search20Regular />, 'aria-hidden': true }}
        freeform
        placeholder="Search companies"
        value={query}
        open={isOpen}
        selectedOptions={[]}
        onChange={(event) => setQuery(event.target.value)}
        onOpenChange={(_event, openData) => {
          setIsOpen(openData.open);
          if (openData.open) {
            setRecentCompanyCodes(getRecentCompanyCodes());
          } else {
            activeCompanyCodeRef.current = null;
          }
        }}
        onActiveOptionChange={(_event, activeData) => {
          const nextValue = activeData.nextOption?.value;
          activeCompanyCodeRef.current =
            nextValue && nextValue !== NO_RESULTS_VALUE ? nextValue : null;
        }}
        onOptionSelect={(_event, optionData) => {
          if (!optionData.optionValue || optionData.optionValue === NO_RESULTS_VALUE) {
            return;
          }
          goToCompany(optionData.optionValue);
        }}
      >
        {renderListboxContent()}
      </Combobox>
    </div>
  );
};

export default StatHeaderSearch;
